import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Layout, Modal, Progress, Result, Spin, Tag, Typography, message } from "antd";
import { ArrowLeftOutlined, RedoOutlined, UndoOutlined } from "@ant-design/icons";
import useOrganization from "../../hooks/useOrganization";
import {
  applyMasterImageRenders,
  getBrickCollection,
  saveMasterImageDraft,
  uploadBrickRenders,
} from "../../utils";
import { applyMasterImage } from "../../masterImages/apply";
import { loadImage, renderRegionPng } from "../../masterImages/canvas";
import { unappliedChanges } from "../../masterImages/changes";
import { detectBricks } from "../../masterImages/detection";
import {
  draftPayload,
  canRedo,
  canUndo,
  draftReducer,
  type DraftAction,
  editedRegionCount,
  initDraft,
  regionFromDetected,
} from "../../masterImages/draft";
import { rotatedSize, scaleRect } from "../../masterImages/geometry";
import type { Brick, BrickCollection, MasterImage } from "../../masterImages/types";
import EditorCanvas, { type StrokePreview, type Tool } from "./EditorCanvas";
import SetupPanel, { type SetupValues } from "./SetupPanel";
import Toolbar from "./Toolbar";
import { useAutosave } from "./useAutosave";
import { ownsKeyboard } from "./keyboard";
import { useOutlines } from "./useOutlines";
import { useWorkingImage } from "./useWorkingImage";

const { Header, Sider, Content } = Layout;

const SAVE_LABELS = { saved: "Saved", pending: "Unsaved changes", saving: "Saving…", error: "Save failed" };

interface EditorProps {
  series: BrickCollection;
  initial: MasterImage;
}

const Editor: React.FC<EditorProps> = ({ series, initial }) => {
  const organization = useOrganization();
  const navigate = useNavigate();
  const [draft, rawDispatch] = useReducer(draftReducer, initial, initDraft);
  // Apply renders the draft as it was when it started; an edit saved meanwhile would make the
  // apply request refer to regions that no longer match.
  const applyingRef = useRef(false);
  const dispatch = useCallback((action: DraftAction) => {
    if (applyingRef.current && action.type !== "select") {
      message.info("Wait until applying has finished.");
      return;
    }
    rawDispatch(action);
  }, []);
  const { masterImage } = draft;
  const [bricks, setBricks] = useState<Brick[]>(series.bricks);
  const [setup, setSetup] = useState<SetupValues>({
    rotation: initial.rotation,
    brickWidthMm: initial.brickWidthMm,
    brickHeightMm: initial.brickHeightMm,
  });
  useEffect(() => {
    setSetup({
      rotation: masterImage.rotation,
      brickWidthMm: masterImage.brickWidthMm,
      brickHeightMm: masterImage.brickHeightMm,
    });
  }, [masterImage.rotation, masterImage.brickWidthMm, masterImage.brickHeightMm]);
  const [tool, setTool] = useState<Tool>("select");
  const [brushRadius, setBrushRadius] = useState(12);
  const [guides, setGuides] = useState(false);
  const [applying, setApplying] = useState<{ done: number; total: number } | null>(null);
  const [modal, modalContext] = Modal.useModal();
  const original = useRef<Promise<HTMLImageElement> | null>(null);

  const { working, error } = useWorkingImage(masterImage, setup.rotation);
  const rotationPending = setup.rotation !== masterImage.rotation;
  const [strokePreview, setStrokePreview] = useState<StrokePreview | null>(null);
  const outlineSource = useMemo(
    () =>
      strokePreview
        ? {
            ...masterImage,
            regions: masterImage.regions.map((r) =>
              r.id === strokePreview.id ? { ...r, strokes: [...r.strokes, strokePreview.stroke] } : r
            ),
          }
        : masterImage,
    [masterImage, strokePreview]
  );
  const outlines = useOutlines(rotationPending ? null : working?.pixels ?? null, working?.scale ?? 1, outlineSource);

  const save = useCallback(
    (mi: MasterImage) => saveMasterImageDraft(organization, series._id, mi._id, draftPayload(mi)),
    [organization, series._id]
  );
  const autosave = useAutosave(masterImage, save);

  const detected = masterImage.brickWidthMm != null;
  const setupChanged =
    rotationPending ||
    setup.brickWidthMm !== masterImage.brickWidthMm ||
    setup.brickHeightMm !== masterImage.brickHeightMm;

  const runDetection = () => {
    if (!working || setup.brickWidthMm == null || setup.brickHeightMm == null) return;
    const result = detectBricks({
      image: working.pixels,
      brickWidthMm: setup.brickWidthMm,
      brickHeightMm: setup.brickHeightMm,
    });
    dispatch({
      type: "redetect",
      rotation: setup.rotation,
      brickWidthMm: setup.brickWidthMm,
      brickHeightMm: setup.brickHeightMm,
      pxPerMm: result.pxPerMm == null ? null : result.pxPerMm / working.scale,
      regions: result.regions.map((r) => regionFromDetected({ ...r, ...scaleRect(r, 1 / working.scale) }, crypto.randomUUID())),
    });
    if (result.regions.length === 0) message.warning("No bricks found. Check the brick size or draw regions by hand.");
  };

  const commitSetup = () => {
    if (!detected && setup.brickWidthMm != null && setup.brickHeightMm != null) redetect();
  };

  const redetect = () => {
    const { regions } = masterImage;
    if (regions.length === 0) return runDetection();
    const edited = editedRegionCount(regions);
    modal.confirm({
      title: "Re-detect bricks?",
      content: `This discards all ${regions.length} brick regions, including ${edited} you edited.`,
      okText: "Re-detect",
      okButtonProps: { danger: true },
      onOk: runDetection,
    });
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (ownsKeyboard(e.target)) return;
      const key = e.key.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && (key === "z" || key === "y")) {
        e.preventDefault();
        dispatch({ type: key === "y" || e.shiftKey ? "redo" : "undo" });
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const step = e.shiftKey ? 10 : 1;
      const moves: Record<string, [number, number]> = {
        ArrowLeft: [-step, 0],
        ArrowRight: [step, 0],
        ArrowUp: [0, -step],
        ArrowDown: [0, step],
      };
      if (moves[e.key]) {
        e.preventDefault();
        dispatch({ type: "move", dx: moves[e.key][0], dy: moves[e.key][1] });
      } else if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        dispatch({ type: "delete" });
      } else if (e.key === "f") {
        dispatch({ type: "flipType" });
      } else if (e.key === "Escape") {
        dispatch({ type: "select", ids: [] });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dispatch]);

  const unapplied = unappliedChanges(masterImage, bricks);
  const selected = useMemo(
    () => masterImage.regions.filter((r) => draft.selection.includes(r.id)),
    [masterImage.regions, draft.selection]
  );

  const apply = async () => {
    applyingRef.current = true;
    setApplying({ done: 0, total: 0 });
    try {
      await autosave.flush();
      original.current ??= loadImage(masterImage.image);
      const image = await original.current;
      const updated = await applyMasterImage(masterImage, bricks, {
        render: (region) => renderRegionPng(image, masterImage, region),
        upload: (blobs) => uploadBrickRenders(organization, series._id, blobs),
        apply: (renders) => applyMasterImageRenders(organization, series._id, masterImage._id, renders),
        onProgress: (done, total) => setApplying({ done, total }),
      });
      setBricks(updated.bricks);
      message.success("Applied to the brick series");
    } catch (e) {
      original.current = null;
      message.error((e as Error).message);
    } finally {
      applyingRef.current = false;
      setApplying(null);
    }
  };

  const back = async () => {
    await autosave.flush().catch(() => undefined);
    navigate(`/${organization}/collections/${series._id}`);
  };

  const resolution = masterImage.pxPerMm;
  const bounds = rotatedSize(masterImage.width, masterImage.height, setup.rotation);

  return (
    <Layout style={{ height: "100vh" }}>
      {modalContext}
      <Header style={{ display: "flex", alignItems: "center", gap: 12, background: "#fff", paddingInline: 16 }}>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={back} aria-label="Back to the brick series" />
        <Typography.Text strong style={{ flex: 1 }}>
          {series.name}: master image
        </Typography.Text>
        <Typography.Text type={autosave.status === "error" ? "danger" : "secondary"}>
          {autosave.status === "error" && autosave.error
            ? `Save failed: ${autosave.error}`
            : SAVE_LABELS[autosave.status]}
        </Typography.Text>
        <Button
          type="text"
          icon={<UndoOutlined />}
          disabled={!canUndo(draft)}
          onClick={() => dispatch({ type: "undo" })}
          aria-label="Undo"
          title="Undo (⌘/Ctrl+Z)"
        />
        <Button
          type="text"
          icon={<RedoOutlined />}
          disabled={!canRedo(draft)}
          onClick={() => dispatch({ type: "redo" })}
          aria-label="Redo"
          title="Redo (⌘/Ctrl+Shift+Z)"
        />
        <Tag>{masterImage.regions.length} regions</Tag>
        {unapplied > 0 ? (
          <Tag color="orange">{unapplied} not applied</Tag>
        ) : masterImage.regions.length === 0 ? (
          <Tag>No regions yet</Tag>
        ) : (
          <Tag color="green">Applied</Tag>
        )}
        <Button
          type="primary"
          onClick={apply}
          loading={applying != null}
          disabled={unapplied === 0 || resolution == null || rotationPending}
        >
          Apply to series
        </Button>
      </Header>
      <Layout>
        <Sider width={340} theme="light" style={{ padding: 16, overflowY: "auto" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <SetupPanel
              values={setup}
              detected={detected}
              changed={setupChanged}
              onChange={setSetup}
              onCommit={commitSetup}
              onRedetect={redetect}
              guides={guides || rotationPending}
              onGuides={setGuides}
            />
            {resolution != null && resolution < 1 && (
              <Alert
                type="error"
                title={`Resolution too low: ${resolution.toFixed(2)} px/mm. Bricks need at least 1 px/mm; use a sharper or closer photo.`}
              />
            )}
            {resolution != null && resolution >= 1 && resolution < 1.5 && (
              <Alert
                type="warning"
                title={`Low resolution: ${resolution.toFixed(2)} px/mm. Bricks may look blurry below 1.5 px/mm.`}
              />
            )}
            {resolution == null && detected && (
              <Alert
                type="info"
                title="No scale yet: detection found no bricks. Draw a full brick with a little joint around it, like detected regions, so the scale is known; Apply needs it."
              />
            )}
            {rotationPending && detected && (
              <Alert type="info" title="Re-detect bricks to apply the new rotation." />
            )}
            <Toolbar
              tool={tool}
              onTool={setTool}
              brushRadius={brushRadius}
              onBrushRadius={setBrushRadius}
              tolerance={masterImage.tolerance}
              selected={selected}
              dispatch={dispatch}
            />
            {applying && applying.total > 0 && (
              <Progress percent={Math.round((applying.done / applying.total) * 100)} />
            )}
          </div>
        </Sider>
        <Content style={{ position: "relative" }}>
          {error && <Result status="error" title="The master image could not be loaded" subTitle={error} />}
          {!error && !working && <Spin style={{ position: "absolute", inset: 0, margin: "auto" }} />}
          {working && (
            <EditorCanvas
              image={working.canvas}
              imageScale={working.scale}
              bounds={bounds}
              regions={masterImage.regions}
              selection={draft.selection}
              outlines={outlines}
              showRegions={!rotationPending}
              tool={tool}
              brushRadius={brushRadius}
              guides={guides || rotationPending}
              dispatch={dispatch}
              onStrokePreview={setStrokePreview}
            />
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

const MasterImageEditor: React.FC = () => {
  const organization = useOrganization();
  const { collectionId, masterImageId } = useParams();
  const [series, setSeries] = useState<BrickCollection | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    getBrickCollection(organization, collectionId).then(setSeries, (e: Error) => setFailed(e.message));
  }, [organization, collectionId]);

  const masterImage = series?.masterImages?.find((m) => m._id === masterImageId);
  if (failed) return <Result status="error" title="The brick series could not be loaded" subTitle={failed} />;
  if (!series) return <Spin style={{ display: "block", marginTop: 120 }} />;
  if (!masterImage) return <Result status="404" title="Master image not found" />;
  return <Editor series={series} initial={masterImage} />;
};

export default MasterImageEditor;
